<template>
  <div class="container mt-4">
    <HMModalHeader :title="title" @close="$emit('close')" />
    <div v-if="solvedQuiz && solvedQuiz.quiz_snapshot">
      <h2 class="my-4">{{ solvedQuiz.student.full_name }}</h2>
      <section
        v-for="(question, index) in solvedQuiz.quiz_snapshot.quiz_questions"
        :key="question.id"
        class="mb-5"
      >
        <div>{{ $t('QUIZZES.LABEL.QUESTION') }} {{ index + 1 }}:</div>
        <hr class="mt-1" />
        <h3>
          {{ question.question_title }}
        </h3>
        <div v-if="question.question_text">
          <HMCustomContent :json="question.question_text" html="" />
        </div>
        <div
          class="d-flex answer d-flex"
          :class="{
            'flex-row': question.question_type === 'single_image',
            'flex-column': question.question_type !== 'single_image',
            'flex-wrap': question.question_type !== 'file_upload',
          }"
        >
          <div v-for="answer in question.answers" :key="answer.id">
            <HMOpinionScalePreview
              v-if="answer.answer_type === 'scale'"
              :scaleSelectedItem="scaleSelectedItem(question.id)"
              :questionRange="getQuestionRange(question)"
              :startText="answer.start_text"
              :endText="answer.end_text"
            />
            <HMCheckBoxGroupPreview
              v-else-if="answer.answer_type === 'check'"
              :selected="studentAnswered(question.id, answer.id)"
              :correct="answer.is_correct"
              :displayCorrect="
                solvedQuiz.quiz.can_students_view_correct_answers
              "
              :label="answer.answer_label"
            />
            <HMImageCheckBoxGroupPreview
              class="mr-2"
              v-else-if="
                answer.answer_type === 'image_check' && answer.answer_image
              "
              :selected="studentAnswered(question.id, answer.id)"
              :correct="answer.is_correct"
              :displayCorrect="
                solvedQuiz.quiz.can_students_view_correct_answers
              "
              :label="answer.answer_label"
              :img="answer.answer_image.thumbnail"
            />
            <div v-else-if="answer.answer_type === 'upload'">
              <div v-if="getStudentAnswer(question.id)">
                <HMFilePreview
                  class="mb-3"
                  v-for="upload in getStudentAnswer(question.id).uploads"
                  :file="upload.file"
                  :key="upload.id"
                />
              </div>
              <div v-else>
                {{ $t('QUIZZES.TEXT.NO_UPLOADS') }}
              </div>
            </div>
            <div v-else-if="answer.answer_type === 'text'">
              <span v-if="getStudentAnswer(question.id)">{{
                getStudentAnswer(question.id).text
              }}</span>
            </div>
            <div
              class="d-flex flex-row justify-content-start align-items-center mt-4"
              v-if="
                getStudentAnswer(question.id) &&
                !question.is_auto_gradable &&
                question.manual_check &&
                displayCorrect
              "
            >
              <HMButton
                v-if="!isReadOnly || isStudentAnswerCorrect(question.id)"
                :disabled="isReadOnly"
                class="mr-3"
                :color="
                  isStudentAnswerCorrect(question.id)
                    ? 'selected'
                    : 'outline-gray'
                "
              >
                <span>
                  <i
                    class="far fa-check mr-2"
                    :class="{
                      'c-coral':
                        isStudentAnswerUngraded(question.id) ||
                        isStudentAnswerIncorrect(question.id),
                    }"
                  ></i>
                  {{ $t('QUIZZES.BUTTON.CORRECT') }}
                </span>
              </HMButton>
              <HMButton
                v-if="!isReadOnly || isStudentAnswerIncorrect(question.id)"
                :disabled="isReadOnly"
                :color="
                  isStudentAnswerIncorrect(question.id)
                    ? 'selected-incorrect'
                    : 'outline-gray'
                "
              >
                <span>
                  <i
                    class="far fa-times mr-2"
                    :class="{
                      'c-coral':
                        isStudentAnswerUngraded(question.id) ||
                        isStudentAnswerCorrect(question.id),
                    }"
                  ></i>
                  {{ $t('QUIZZES.BUTTON.INCORRECT') }}
                </span>
              </HMButton>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import HMButton from '@/web/components/common/HMButton';
import HMFilePreview from '@/web/components/common/HMFilePreview';

export default {
  name: 'StudentQuizSubmissionViewer',
  components: { HMFilePreview, HMButton },
  props: {
    solvedQuiz: {
      type: Object,
    },
  },
  computed: {
    submissionScore() {
      if (!this.solvedQuiz) return '';

      const score = this.solvedQuiz.grade_achieved_score || 0;
      return `${score}%`;
    },
    allAnswersGraded() {
      if (!this.solvedQuiz?.student_answers) return true;
      return this.solvedQuiz.student_answers.every(
        answer =>
          answer.is_correct !== null ||
          this.questionNotGradable(answer.quiz_question)
      );
    },
    isQuizGradable() {
      return this.solvedQuiz?.quiz_snapshot?.is_gradable;
    },
    displayCorrect() {
      return this.solvedQuiz.quiz.can_students_view_correct_answers;
    },
    isReadOnly() {
      return true;
    },
    title() {
      return `${this.solvedQuiz.quiz.quiz_name} ${
        this.isQuizGradable ? `(${this.submissionScore})` : ''
      }`;
    },
  },
  methods: {
    questionNotGradable(questionId) {
      const questions = this.solvedQuiz.quiz_snapshot.quiz_questions.filter(
        q => q.id === questionId
      );
      if (questions && questions.length) {
        return !questions[0].manual_check;
      }
      return false;
    },
    getStudentAnswers(questionId) {
      return this.solvedQuiz.student_answers.filter(
        a => a.quiz_question === questionId
      );
    },
    getStudentAnswer(questionId) {
      const answers = this.getStudentAnswers(questionId);
      if (!answers.length) return null;
      return answers[0];
    },
    isStudentAnswerCorrect(questionId) {
      const answer = this.getStudentAnswer(questionId);
      if (!answer) return false;
      return answer.is_correct === true;
    },
    isStudentAnswerIncorrect(questionId) {
      const answer = this.getStudentAnswer(questionId);
      if (!answer) return false;
      return answer.is_correct === false;
    },
    isStudentAnswerUngraded(questionId) {
      const answer = this.getStudentAnswer(questionId);
      if (!answer) return false;
      return answer.is_correct === null;
    },
    studentAnswered(questionId, answerId) {
      return (
        this.solvedQuiz.student_answers.filter(
          a => a.quiz_question === questionId && a.selected_answer === answerId
        ).length > 0
      );
    },
    scaleSelectedItem(questionId) {
      const answer = this.getStudentAnswer(questionId);
      if (answer) {
        return answer.range;
      }
    },
    getQuestionRange(question) {
      const answer = question.answers[0];
      if (answer) {
        return answer.range;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.correct-answer {
  background-color: lighten($light-green, 20%);
}
</style>
